import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLoading } from "@/contexts/LoadingContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const { triggerLoading, isLoading } = useLoading();
  const { t } = useTranslation();

  const handleLogoClick = () => {
    triggerLoading();
  };

  // Reset animation when loading completes
  useEffect(() => {
    if (!isLoading) {
      setAnimationKey(prev => prev + 1);
    }
  }, [isLoading]);

  useEffect(() => {
    // Handle hash navigation on mount
    if (window.location.hash) {
      setTimeout(() => {
        const element = document.getElementById(window.location.hash.slice(1));
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, []);

  const navLinks = [
    { name: t("nav.home"), href: "#home" },
    { name: t("nav.villasPlots"), href: "#layouts" },
    { name: t("nav.masterPlan"), href: "#masterplan" },
    { name: t("nav.theVision"), href: "#features" },
    { name: t("nav.location"), href: "#location" },
    { name: t("nav.exploreHuaHin"), href: "#explore" },
    { name: t("nav.theDeveloper"), href: "#references" },
    { name: t("nav.qa"), href: "#faq" },
    { name: t("nav.contact"), href: "#contact" },
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    
    if (href === "#home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      window.history.pushState(null, "", "/");
    } else {
      const element = document.getElementById(href.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.pushState(null, "", href);
      }
    }
    
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border/30"
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <div 
            onClick={handleLogoClick}
            className="flex flex-col cursor-pointer flex-shrink-0"
          >
            <span className="font-serif tracking-[0.12em] text-primary leading-none text-center" style={{ fontSize: '1.8rem', marginBottom: '2px', fontWeight: 300, fontSynthesis: 'none' }}>
              SIAM
            </span>
            <div className="flex items-center justify-center">
              <div 
                key={`left-${animationKey}`} 
                className="h-[1px] origin-right nav-logo-line-left"
                style={{ 
                  background: 'linear-gradient(to left, hsl(var(--primary) / 0.6) 75%, transparent 100%)',
                  width: '75px'
                }}
              />
              <div 
                key={`right-${animationKey}`} 
                className="h-[1px] origin-left nav-logo-line-right"
                style={{ 
                  background: 'linear-gradient(to right, hsl(var(--primary) / 0.6) 75%, transparent 100%)',
                  width: '75px'
                }}
              />
            </div>
            <span className="font-serif tracking-[0.12em] text-primary leading-none" style={{ fontSize: '1.8rem', marginTop: '2px', fontWeight: 300, fontSynthesis: 'none' }}>
              HORIZON
            </span>
          </div>
          <style>{`
            .nav-logo-line-left {
              transform: scaleX(0);
              animation: navLogoExpandLeft 1.5s ease-out 0.5s forwards;
            }
            .nav-logo-line-right {
              transform: scaleX(0);
              animation: navLogoExpandRight 1.5s ease-out 0.5s forwards;
            }
            @keyframes navLogoExpandLeft {
              from { transform: scaleX(0); }
              to { transform: scaleX(1); }
            }
            @keyframes navLogoExpandRight {
              from { transform: scaleX(0); }
              to { transform: scaleX(1); }
            }
          `}</style>

          {/* Desktop Navigation Links - centered */}
          <div className="hidden md:flex items-center space-x-8 lg:space-x-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="group relative font-body text-xs font-normal tracking-widest uppercase transition-elegant text-foreground hover:text-accent cursor-pointer pb-1"
              >
                {link.name}
                <span className="absolute bottom-0 left-1/2 flex -translate-x-1/2">
                  <span 
                    className="h-[1px] transition-all duration-300 w-0 group-hover:w-8"
                    style={{ 
                      background: 'linear-gradient(to left, hsl(var(--accent)) 75%, transparent 100%)'
                    }}
                  />
                  <span 
                    className="h-[1px] transition-all duration-300 w-0 group-hover:w-8"
                    style={{ 
                      background: 'linear-gradient(to right, hsl(var(--accent)) 75%, transparent 100%)'
                    }}
                  />
                </span>
              </a>
            ))}
          </div>

          {/* Language Switcher - anchored right */}
          <div className="hidden md:flex items-center flex-shrink-0">
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-0">
            <LanguageSwitcher />
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <button className="text-primary min-h-[48px] min-w-[48px] flex items-center justify-center" aria-label="Open navigation menu">
                  <Menu size={28} strokeWidth={2.5} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-[320px] bg-background backdrop-blur-none p-0 border-l border-border/30">
                <SheetHeader className="px-6 pt-8 pb-4 border-b border-border/30">
                  <SheetTitle className="font-serif text-2xl tracking-wider text-primary">
                    SIAM HORIZON
                  </SheetTitle>
                </SheetHeader>
                <nav className="px-6 py-6 flex flex-col gap-1">
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      className="block py-4 font-body text-base tracking-widest uppercase text-primary hover:text-accent active:text-accent transition-elegant cursor-pointer border-b border-border/20"
                    >
                      {link.name}
                    </a>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
